import axios from 'axios';
import Cookies from 'js-cookie';

// Create axios instance with dynamic baseURL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASEURL || '', // Use env var in prod, fallback to proxy in dev
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Authorization header with token from cookies
api.interceptors.request.use(
  (config) => {
    const token = Cookies.get('quotes_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Endpoints involved in the OTP login flow itself (requesting/verifying/refreshing a
// code before a session exists). A 401 from these means "bad or expired OTP/refresh
// token", not "your existing session was invalidated" - so they must not trigger the
// same-page logout behavior below (which would otherwise wipe out an unrelated,
// still-valid session cookie or bounce the user out of the login screen mid-flow).
const OTP_AUTH_ENDPOINTS = [
  'api/auth/otp/request',
  'api/auth/otp/verify',
  'api/auth/otp/refresh',
];

function isOtpAuthEndpoint(config) {
  const url = config?.url || '';
  return OTP_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

// Response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isOtpAuthEndpoint(error.config)) {
      Cookies.remove('quotes_token');
      // Notify the rest of the app (AuthContext) that the session is no longer valid
      // so protected routes can redirect to login naturally off context state.
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

// ===========================
// Quote Request Messaging API
// ===========================

/**
 * Post a message to a quote request with optional file attachments
 * @param {number} requestId - The quote request ID
 * @param {Object} messageData - Message data
 * @param {string} messageData.body - HTML message content
 * @param {string} [messageData.subject] - Optional subject
 * @param {boolean} [messageData.is_note] - Whether this is an internal note (default: false)
 * @param {string} [messageData.message_type] - Message type (default: 'comment')
 * @param {File[]} [messageData.files] - Optional array of files to attach
 * @returns {Promise} API response with message object
 */
export const postQuoteRequestMessage = (requestId, messageData) => {
  // If files are present, use FormData
  if (messageData.files && messageData.files.length > 0) {
    const formData = new FormData();
    formData.append('body', messageData.body);
    if (messageData.subject) formData.append('subject', messageData.subject);
    formData.append('is_note', messageData.is_note || false);
    formData.append('message_type', messageData.message_type || 'comment');

    // Append files
    messageData.files.forEach(file => {
      formData.append('files', file);
    });

    return api.post(`/api/quote-requests/${requestId}/messages`, formData, {
      headers: { 'Content-Type': undefined } // Let browser set Content-Type with boundary
    });
  }

  // Otherwise use JSON
  return api.post(`/api/quote-requests/${requestId}/messages`, messageData);
};

/**
 * Get messages for a quote request
 * @param {number} requestId - The quote request ID
 * @param {Object} [params] - Query parameters
 * @param {number} [params.limit] - Number of messages to fetch (default: 50)
 * @param {number} [params.offset] - Offset for pagination (default: 0)
 * @param {boolean} [params.only_notes] - Filter only internal notes (default: false)
 * @returns {Promise} API response with messages array and pagination info
 */
export const getQuoteRequestMessages = (requestId, params = {}) => {
  return api.get(`/api/quote-requests/${requestId}/messages`, { params });
};

/**
 * Post a message to a quote request line with optional file attachments
 * @param {number} lineId - The quote line ID
 * @param {Object} messageData - Message data
 * @param {string} messageData.body - HTML message content
 * @param {string} [messageData.subject] - Optional subject
 * @param {boolean} [messageData.is_note] - Whether this is an internal note (default: false)
 * @param {string} [messageData.message_type] - Message type (default: 'comment')
 * @param {File[]} [messageData.files] - Optional array of files to attach
 * @returns {Promise} API response with message object
 */
export const postQuoteLineMessage = (lineId, messageData) => {
  // If files are present, use FormData
  if (messageData.files && messageData.files.length > 0) {
    const formData = new FormData();
    formData.append('body', messageData.body);
    if (messageData.subject) formData.append('subject', messageData.subject);
    formData.append('is_note', messageData.is_note || false);
    formData.append('message_type', messageData.message_type || 'comment');

    // Append files
    messageData.files.forEach(file => {
      formData.append('files', file);
    });

    return api.post(`/api/quote-lines/${lineId}/messages`, formData, {
      headers: { 'Content-Type': undefined } // Let browser set Content-Type with boundary
    });
  }

  // Otherwise use JSON
  return api.post(`/api/quote-lines/${lineId}/messages`, messageData);
};

/**
 * Get messages for a quote request line
 * @param {number} lineId - The quote line ID
 * @param {Object} [params] - Query parameters
 * @param {number} [params.limit] - Number of messages to fetch (default: 50)
 * @param {number} [params.offset] - Offset for pagination (default: 0)
 * @param {boolean} [params.only_notes] - Filter only internal notes (default: false)
 * @returns {Promise} API response with messages array and pagination info
 */
export const getQuoteLineMessages = (lineId, params = {}) => {
  return api.get(`/api/quote-lines/${lineId}/messages`, { params });
};

/**
 * Get remarks for a quote line
 * @param {number} lineId - The quote line ID
 * @returns {Promise} API response with line remarks
 */
export const getQuoteLineRemarks = (lineId) => {
  return api.get(`/api/quote-lines/${lineId}/remarks`);
};

/**
 * Update remarks for a quote line
 * @param {number} lineId - The quote line ID
 * @param {string} remarks - Updated remarks text
 * @returns {Promise} API response with updated remarks
 */
export const updateQuoteLineRemarks = (lineId, remarks) => {
  return api.post(`/api/quote-lines/${lineId}/remarks`, { remarks });
};

// ===========================
// Certificate Management API
// ===========================

/**
 * Upload certificate for a quote line
 * @param {number} lineId - The quote line ID
 * @param {File} file - Certificate file to upload
 * @returns {Promise} API response with upload status
 */
export const uploadQuoteLineCertificate = (lineId, file) => {
  const formData = new FormData();
  formData.append('certificate', file);

  return api.post(`/api/quote-lines/${lineId}/certificate`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Get certificate info for a quote line
 * @param {number} lineId - The quote line ID
 * @returns {Promise} API response with certificate info
 */
export const getQuoteLineCertificate = (lineId) => {
  return api.get(`/api/quote-lines/${lineId}/certificate`);
};

/**
 * Download certificate for a quote line
 * @param {number} lineId - The quote line ID
 * @returns {Promise} API response with file download
 */
export const downloadQuoteLineCertificate = (lineId) => {
  return api.get(`/api/quote-lines/${lineId}/certificate/download`, {
    responseType: 'blob',
  });
};

/**
 * Delete certificate for a quote line
 * @param {number} lineId - The quote line ID
 * @returns {Promise} API response with deletion status
 */
export const deleteQuoteLineCertificate = (lineId) => {
  return api.delete(`/api/quote-lines/${lineId}/certificate`);
};

/**
 * Get all lines with message counts for a quote request
 * @param {number} requestId - The quote request ID
 * @returns {Promise} API response with lines array including message counts and attachment counts
 */
export const getQuoteLinesWithMessages = (requestId) => {
  return api.get(`/api/quote-requests/${requestId}/lines-with-messages`);
};

// ===========================
// Attachment Management API
// ===========================

/**
 * Upload attachments to a quote request
 * @param {number} requestId - The quote request ID
 * @param {File[]} files - Array of files to upload
 * @returns {Promise} API response with uploaded attachments
 */
export const uploadQuoteRequestAttachments = (requestId, files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  return api.post(`/api/quote-requests/${requestId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

/**
 * Get all attachments for a quote request
 * @param {number} requestId - The quote request ID
 * @returns {Promise} API response with attachments array
 */
export const getQuoteRequestAttachments = (requestId) => {
  return api.get(`/api/quote-requests/${requestId}/attachments`);
};

/**
 * Upload attachments to a quote line
 * @param {number} lineId - The quote line ID
 * @param {File[]} files - Array of files to upload
 * @returns {Promise} API response with uploaded attachments
 */
export const uploadQuoteLineAttachments = (lineId, files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  return api.post(`/api/quote-lines/${lineId}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

/**
 * Get all attachments for a quote line
 * @param {number} lineId - The quote line ID
 * @returns {Promise} API response with attachments array
 */
export const getQuoteLineAttachments = (lineId) => {
  return api.get(`/api/quote-lines/${lineId}/attachments`);
};

/**
 * Delete an attachment
 * @param {number} attachmentId - The attachment ID
 * @returns {Promise} API response confirming deletion
 */
export const deleteAttachment = (attachmentId) => {
  return api.delete(`/api/attachments/${attachmentId}/delete`);
};

// ===========================
// Quote Request Actions API
// ===========================

/**
 * Duplicate a quote request
 * @param {number} requestId - The quote request ID to duplicate
 * @param {Object} [options] - Duplication options
 * @param {boolean} [options.set_in_progress] - Set to in_progress state (default: true)
 * @param {boolean} [options.reset_user] - Reset portal user (default: false)
 * @param {number} [options.extend_deadline_days] - Extend deadline by N days (default: 2)
 * @returns {Promise} API response with original and duplicated request
 */
export const duplicateQuoteRequest = (requestId, options = {}) => {
  return api.post(`/api/quote-requests/${requestId}/duplicate`, options);
};

/**
 * Set quote request to in_progress state
 * @param {number} requestId - The quote request ID
 * @returns {Promise} API response with updated quote request
 */
export const setQuoteRequestInProgress = (requestId) => {
  return api.post(`/api/quote-requests/${requestId}/set-in-progress`);
};

/**
 * Complete a quote request (all lines must be completed first)
 * @param {number} requestId - The quote request ID
 * @returns {Promise} API response with completed quote request
 */
export const completeQuoteRequest = (requestId) => {
  return api.post(`/api/quote-requests/${requestId}/complete`);
};

/**
 * Cancel a quote request
 * @param {number} requestId - The quote request ID
 * @returns {Promise} API response with cancelled quote request
 */
export const cancelQuoteRequest = (requestId) => {
  return api.post(`/api/quote-requests/${requestId}/cancel`);
};

/**
 * Get all quote requests assigned to current user
 * @param {Object} [params] - Query parameters
 * @param {string} [params.state] - Filter by state (draft/assigned/in_progress/completed/cancelled)
 * @param {number} [params.limit] - Number of records per page (default: 50)
 * @param {number} [params.offset] - Offset for pagination (default: 0)
 * @returns {Promise} API response with user's quote requests
 */
export const getUserQuoteRequests = (params = {}) => {
  return api.get('/api/quote-requests/user-requests', { params });
};

// ===========================
// Quote Line Version API
// ===========================

/**
 * Create a new version of a quote line
 * @param {number} lineId - The quote line ID to create a version from
 * @returns {Promise} API response with new version and original version data
 */
export const createQuoteLineVersion = (lineId) => {
  return api.post(`/api/quote-lines/${lineId}/create-version`);
};

/**
 * Get version history for a quote line
 * @param {number} lineId - The quote line ID (any version in the chain)
 * @returns {Promise} API response with versions array and metadata
 */
export const getQuoteLineVersionHistory = (lineId) => {
  return api.get(`/api/quote-lines/${lineId}/version-history`);
};

/**
 * Get version info for a specific quote line
 * @param {number} lineId - The quote line ID
 * @returns {Promise} API response with version information
 */
export const getQuoteLineVersionInfo = (lineId) => {
  return api.get(`/api/quote-lines/${lineId}/version-info`);
};

export default api;
